import React from 'react'
import { AnimatePresence } from '@tamagui/animate-presence'
import { Button, Image, XStack, YStack, styled } from 'tamagui'
import { AntDesign } from '@expo/vector-icons'
import { normalizeRemoteImageUriForNative } from '@utils/normalizeRemoteImageUriForNative'

const GalleryItem = styled(YStack, {
  zIndex: 1,
  x: 0,
  opacity: 1,
  fullscreen: true,

  variants: {
    // 1 = right, 0 = nowhere, -1 = left
    going: {
      ':number': (going) => ({
        enterStyle: {
          x: going > 0 ? 1000 : -1000,
          opacity: 0,
        },
        exitStyle: {
          zIndex: 0,
          x: going < 0 ? 1000 : -1000,
          opacity: 0,
        },
      }),
    },
  } as const,
})

const wrap = (min: number, max: number, v: number) => {
  const rangeSize = max - min
  if (rangeSize <= 0) return min
  return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min
}

function carouselImageSrc(entry: unknown): string {
  if (typeof entry === 'string' && entry.trim()) {
    return normalizeRemoteImageUriForNative(entry.trim());
  }
  if (entry && typeof entry === 'object') {
    const o = entry as { uri?: string; url?: string; src?: string };
    const raw = (o.uri || o.url || o.src || '').trim();
    if (raw) return normalizeRemoteImageUriForNative(raw);
  }
  return '';
}

export default function HorizontalCarousel({ autoPlay, imageStyle, images, height, width: frameWidth, removeSampleMark, fill, imageResizeMode }: {
  imageStyle?: any,
  images: string[],
  height?: number,
  /** Fixed frame width — use in list cards so images stay contained instead of stretching to 100%. */
  width?: number,
  autoPlay?: boolean,
  removeSampleMark?: boolean,
  /** Fill a 16:9 (or other) parent box instead of a fixed pixel height. */
  fill?: boolean,
  imageResizeMode?: 'cover' | 'contain' | 'stretch',
}) {
  const [[page, going], setPage] = React.useState([0, 0])
  const [galleryImages, setGalleryImages] = React.useState(images);
  const [imageLoading, setImageLoading] = React.useState(true);
  const resolvedImages = React.useMemo(
    () => (Array.isArray(images) ? images.map(carouselImageSrc).filter(Boolean) : []),
    [images]
  );

  const imageIndex = wrap(0, resolvedImages.length, page)
  const paginate = (going: number) => {
    setPage([page + going, going])
  }

  const stackWidth = fill ? '100%' : (frameWidth != null ? frameWidth : '100%');
  const stackHeight = fill ? '100%' : (height || 180);
  const imageFit = imageResizeMode === 'contain' ? 'contain' : 'cover';
  const containBox = {
    maxWidth: '100%' as const,
    minWidth: 0,
    minHeight: 0,
  };
  const imageDims = fill
    ? { width: '100%' as const, height: '100%' as const, ...containBox }
    : frameWidth != null
      ? { width: frameWidth, height: height || 100 }
      : { width: '100%' as const, height: height || 100, ...containBox };

  if (resolvedImages.length === 1) {
    return (
      <XStack
        overflow="hidden"
        backgroundColor="$colorTransparent"
        height={stackHeight}
        width={stackWidth}
        maxWidth="100%"
        minWidth={0}
        alignSelf="stretch"
        alignItems="center"
        justifyContent="center"
      >
        <Image
          {...imageDims}
          backgroundColor="$colorTransparent"
          resizeMode={imageFit}
          objectFit={imageFit}
          src={resolvedImages[0]}
          style={[{ maxWidth: '100%', minWidth: 0, minHeight: 0 }, imageStyle]}
        />
      </XStack>
    )
  }

  return (
    <XStack
      overflow="hidden"
      backgroundColor="$colorTransparent"
      height={stackHeight}
      width={stackWidth}
      maxWidth="100%"
      minWidth={0}
      alignSelf="stretch"
      alignItems="center"
      justifyContent="center"
    >
      <AnimatePresence initial={false} custom={{ going }}>
        <GalleryItem key={page + imageLoading.toString()} animation="slow" going={going}>
          <Image
            {...imageDims}
            backgroundColor="$colorTransparent"
            resizeMode={imageFit}
            objectFit={imageFit}
            src={resolvedImages[imageIndex]}
            style={[{ maxWidth: '100%', minWidth: 0, minHeight: 0 }, imageStyle]}
          />
        </GalleryItem>
      </AnimatePresence>
      {
        resolvedImages.length > 1 && (
          <>
            <Button
              accessibilityLabel="Carousel left"
              icon={<AntDesign name="arrow-left" size={24} color="white" />}
              size="$3"
              position="absolute"
              left={-5}
              borderStartStartRadius={0}
              borderEndStartRadius={0}
              circular
              elevate
              onPress={() => paginate(-1)}
              zIndex={100}
            />

            <Button
              accessibilityLabel="Carousel right"
              icon={<AntDesign name="arrow-right" size={24} color="white" />}
              size="$3"
              position="absolute"
              right={-5}
              borderEndEndRadius={0}
              borderStartEndRadius={0}
              circular
              elevate
              onPress={() => paginate(1)}
              zIndex={100}
            />
          </>)
      }
    </XStack>
  )
}